import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Button } from '@/shared/ui/Button'
import { Field } from '@/shared/ui/Field'
import { Modal } from '@/shared/ui/Modal'
import { Pagination } from '@/shared/ui/Pagination'
import { Empty, Panel } from '@/shared/ui/Panel'
import { Segmented } from '@/shared/ui/Segmented'
import { Strip } from '@/shared/ui/Strip'
import type { AuditEntryResponse, OnchainTxResponse } from '@/shared/api/types'
import { explain } from '@/shared/lib/errors'
import { crypto, shortAddress } from '@/shared/lib/format'
import { useMeta } from '@/shared/lib/hooks/useMeta'
import type { OpsTab } from '@/stores/AdminOpsStore'
import { useStores } from '@/stores/RootStore'
import page from './Page.module.css'
import styles from './Admin.module.css'

const TABS: { value: OpsTab; label: string }[] = [
  { value: 'unmatched', label: 'Поступления' },
  { value: 'audit', label: 'Журнал' },
  { value: 'release', label: 'Застрявший вывод' },
]

/**
 * Операционная админка.
 *
 * Три вещи, которые в макете не нарисованы, а ручки под них есть,
 * и без них шлюз некому обслуживать:
 *
 *  - поступления, не привязавшиеся ни к одной заявке — это лежащие
 *    в базе чужие деньги;
 *  - журнал действий администраторов — единственный способ разобрать,
 *    кто и когда сбросил чужой второй фактор;
 *  - снятие закрепления с вывода, который никогда не подтвердится.
 */
export const AdminOpsPage = observer(function AdminOpsPage() {
  useMeta('Операции')
  const { adminOps: store } = useStores()

  useEffect(() => { void store.unmatched.load() }, [store])

  return (
    <div className={page.wide}>
      <h1 className={styles.h1}>Операции</h1>

      <Segmented ariaLabel="Раздел операций" options={TABS} value={store.tab}
                 onChange={(tab) => store.setTab(tab)} />

      {store.tab === 'unmatched' ? <Unmatched /> : null}
      {store.tab === 'audit' ? <Audit /> : null}
      {store.tab === 'release' ? <ReleasePin /> : null}

      <OrphanDialog />
    </div>
  )
})

const Unmatched = observer(function Unmatched() {
  const { adminOps: store } = useStores()
  const items = store.unmatched.value?.items ?? []

  return (
    <Panel title="Несопоставленные поступления">
      {store.unmatched.error && !store.unmatched.loaded ? (
        <Strip tone="bad"
               aside={<Button variant="secondary" onClick={() => void store.unmatched.load()}>Ещё раз</Button>}>
          {explain(store.unmatched.error)}
        </Strip>
      ) : null}

      {items.length === 0 && store.unmatched.loaded ? (
        <Empty title="Всё сопоставлено" />
      ) : (
        <>
          <ul className={styles.queue}>
            {items.map((tx) => <OnchainRow key={tx.id} tx={tx} />)}
          </ul>
          <Pagination total={store.unmatched.value?.total ?? 0} limit={store.limit}
                      offset={store.unmatchedOffset}
                      onChange={(offset) => store.goUnmatched(offset)} />
        </>
      )}
    </Panel>
  )
})

const OnchainRow = observer(function OnchainRow({ tx }: { tx: OnchainTxResponse }) {
  const { adminOps: store } = useStores()
  return (
    <li className={styles.queueRow}>
      <span className={styles.queueMain}>
        <span className={styles.queueAmount}>
          {crypto(tx.amount)} {tx.currency.toUpperCase()}
        </span>
        <span className={styles.queueMeta}>
          {tx.chain} · {shortAddress(tx.address, 10, 6)} · {tx.confirmations}/{tx.required_confirmations}
        </span>
        <span className={styles.queueMeta}>
          TxID {shortAddress(tx.txid, 12, 8)} · {when(tx.first_seen_at)}
        </span>
      </span>
      <span className={styles.queueActs}>
        <Button variant="secondary" onClick={() => store.askOrphan(tx)}>Сторнировать</Button>
      </span>
    </li>
  )
})

const Audit = observer(function Audit() {
  const { adminOps: store } = useStores()
  const items = store.audit.value?.items ?? []

  return (
    <Panel title="Журнал действий">
      {items.length === 0 && store.audit.loaded ? (
        <Empty title="Записей нет" />
      ) : (
        <>
          <ul className={styles.queue}>
            {items.map((entry) => <AuditRow key={entry.id} entry={entry} />)}
          </ul>
          <Pagination total={store.audit.value?.total ?? 0} limit={store.limit}
                      offset={store.auditOffset}
                      onChange={(offset) => store.goAudit(offset)} />
        </>
      )}
    </Panel>
  )
})

function AuditRow({ entry }: { entry: AuditEntryResponse }) {
  const details = Object.entries(entry.details ?? {})
  return (
    <li className={styles.queueRow}>
      <span className={styles.queueMain}>
        <span className={styles.queueAmount}>{ACTIONS[entry.action] ?? entry.action}</span>
        <span className={styles.queueMeta}>
          {entry.actor_email}
          {entry.target_email ? ` → ${entry.target_email}` : ''}
          {entry.ip ? ` · ${entry.ip}` : ''}
        </span>
        <span className={styles.queueMeta}>{when(entry.created_at)}</span>
        {/* Подробности показываются как есть: набор полей у каждого
            действия свой, и придумывать им общий вид значит однажды
            спрятать то единственное поле, ради которого журнал и читают. */}
        {details.length > 0 ? (
          <span className={styles.queueMeta}>
            {details.map(([k, v]) => `${k}: ${String(v)}`).join(' · ')}
          </span>
        ) : null}
      </span>
    </li>
  )
}

/** Понятные имена действий. Незнакомое показывается как есть. */
const ACTIONS: Record<string, string> = {
  account_created: 'Создан аккаунт',
  account_updated: 'Изменён аккаунт',
  account_deleted: 'Удалён аккаунт',
  twofa_reset: 'Сброшен второй фактор',
  withdrawal_approved: 'Вывод одобрен',
  withdrawal_rejected: 'Вывод отклонён',
  withdrawal_pin_released: 'Снято закрепление вывода',
  onchain_orphaned: 'Поступление сторнировано',
}

const ReleasePin = observer(function ReleasePin() {
  const { adminOps: store } = useStores()

  return (
    <Panel title="Снять закрепление с вывода">
      {/* Самое опасное действие в шлюзе: после него те же байты могут
          уйти в сеть повторно. Предупреждение стоит выше формы. */}
      <Strip tone="bad">
        Если прежняя транзакция подтвердится, деньги уйдут дважды
      </Strip>

      <Field label="Номер заявки" placeholder="UUID заявки на вывод"
             value={store.pinId} onChange={(e) => store.setPinId(e.target.value)}
             error={store.errors.id ?? null} />

      {/* TxID вводится руками намеренно: бекенд требует его целиком,
          и это защита не от подмены, а от промаха по строке. Поэтому
          нигде рядом его и не подставляем. */}
      <Field label="TxID транзакции"
             value={store.txid} onChange={(e) => store.setTxid(e.target.value)}
             error={store.errors.txid ?? null} />

      <Field label="Причина"
             value={store.reason} onChange={(e) => store.setReason(e.target.value)}
             error={store.errors.reason ?? null} />

      <Button variant="primary" block busy={store.busy}
              onClick={() => void store.releasePin()}>
        Снять закрепление
      </Button>
    </Panel>
  )
})

const OrphanDialog = observer(function OrphanDialog() {
  const { adminOps: store } = useStores()
  const tx = store.orphaning
  if (tx === null) return null

  return (
    <Modal title="Сторнировать поступление?" onClose={() => store.close()}
           actions={
             <>
               <Button variant="secondary" onClick={() => store.close()}>Отмена</Button>
               <Button variant="primary" busy={store.busy}
                       onClick={() => void store.orphan()}>Да, сторнировать</Button>
             </>
           }>
      <p className={styles.ask}>
        {crypto(tx.amount)} {tx.currency.toUpperCase()} на {shortAddress(tx.address, 8, 6)}
      </p>

      {/* TxID НЕ подставляется из строки и не показывается рядом.
          Бекенд требует его вручную ровно затем, чтобы промах
          по строке в списке не приводил к сторно чужого поступления, —
          подставленное значение свело бы эту защиту на нет. */}
      <Field label="TxID поступления"
             value={store.txid} onChange={(e) => store.setTxid(e.target.value)}
             error={store.errors.txid ?? null} />

      <Field label="Причина"
             value={store.reason} onChange={(e) => store.setReason(e.target.value)}
             error={store.errors.reason ?? null} />
    </Modal>
  )
})

function when(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(date).replace(', ', ' / ')
}
